// Bridge between `whereAndParams` and the sql.js-httpvfs worker.
//
// Mirrors the SELECT_COLS + FROM_JOINS + ORDER BY shape from
// queries.py::query_events. One round-trip for COUNT, a second for the
// paged page. Watch links via the per-game clock map sidecar aren't
// ported in PR C (sidecars aren't shipped to the browser yet); the
// events-table renderer just omits the watch column when
// `youtube_video_id` is set but no offset is available.

import { getDbWorker } from "./db";
import type { Filters } from "./filters";
import { whereAndParams } from "./filters";

// Mirrors queries.py SELECT_COLS exactly — same alias names so the rest of
// the JS port can read fields by their original keys.
const SELECT_COLS = `
    e.event_id, e.game_id, g.date_played, g.home_team_id, g.away_team_id,
    ht.code AS home_code, at.code AS away_code,
    e.period, e.time_remaining, e.event_type, e.zone, e.x_loc, e.y_loc,
    e.team_id, tt.code AS team_code,
    e.player_id, p.first_name || ' ' || p.last_name AS player_name,
    e.shot_type, e.shot_quality, e.is_goal, e.xg, e.score_diff_for,
    e.strength_state, e.clock_seconds_elapsed,
    e.pressure_sequence_tag,
    g.youtube_video_id,
    pc.terminator_event_type AS chain_terminator,
    pc.num_events AS chain_num_events,
    pc.started_off_faceoff AS chain_off_faceoff,
    pc.started_off_rush AS chain_off_rush,
    e.hit_recipient_player_id, hr.first_name || ' ' || hr.last_name AS hit_recipient_name,
    e.penalty_drawn_player_id, pd.first_name || ' ' || pd.last_name AS penalty_drawn_name
`.trim();

const FROM_JOINS = `
    FROM events e
    JOIN games g ON g.game_id = e.game_id
    LEFT JOIN teams ht ON ht.team_id = g.home_team_id
    LEFT JOIN teams at ON at.team_id = g.away_team_id
    LEFT JOIN teams tt ON tt.team_id = e.team_id
    LEFT JOIN players p ON p.player_id = e.player_id
    LEFT JOIN possession_chains pc ON pc.chain_id = e.possession_chain_id
    LEFT JOIN players hr ON hr.player_id = e.hit_recipient_player_id
    LEFT JOIN players pd ON pd.player_id = e.penalty_drawn_player_id
`.trim();

export interface EventRow {
  event_id: number;
  game_id: number;
  date_played: string | null;
  home_team_id: number | null;
  away_team_id: number | null;
  home_code: string | null;
  away_code: string | null;
  period: number | null;
  time_remaining: string | null;
  event_type: string;
  zone: string | null;
  x_loc: number | null;
  y_loc: number | null;
  team_id: number | null;
  team_code: string | null;
  player_id: number | null;
  player_name: string | null;
  shot_type: string | null;
  shot_quality: string | null;
  is_goal: number;
  xg: number | null;
  score_diff_for: number | null;
  strength_state: string | null;
  clock_seconds_elapsed: number;
  pressure_sequence_tag: number | null;
  youtube_video_id: string | null;
  hit_recipient_player_id: number | null;
  hit_recipient_name: string | null;
  penalty_drawn_player_id: number | null;
  penalty_drawn_name: string | null;
}

export interface QueryResult {
  rows: EventRow[];
  total: number;
}

export async function runEventsQuery(filters: Filters): Promise<QueryResult> {
  const { where, params } = whereAndParams(filters);
  const worker = await getDbWorker();

  const countSql = `SELECT COUNT(*) AS n ${FROM_JOINS} WHERE ${where}`;
  const countRows = (await worker.db.query(countSql, params)) as Array<{ n: number }>;
  const total = countRows[0]?.n ?? 0;

  const pageSql = `
      SELECT ${SELECT_COLS}
      ${FROM_JOINS}
      WHERE ${where}
      ORDER BY g.date_played DESC, e.clock_seconds_elapsed DESC
      LIMIT ? OFFSET ?
  `.trim();
  const pageParams = [...params, filters.limit, filters.offset];
  const rows = (await worker.db.query(pageSql, pageParams)) as EventRow[];
  return { rows, total };
}

/** Returns the total bytes the worker has range-fetched so far. Useful
 *  for the dev console; the events-table renderer doesn't display it. */
export async function bytesFetched(): Promise<number> {
  const worker = await getDbWorker();
  return worker.worker.bytesRead;
}
